import React, { useState, useEffect, Suspense } from 'react';
import { lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { StandardHeader } from './components/StandardHeader';
import { MobileBottomNav } from './components/MobileBottomNav';
import { AvatarDropdown } from './components/AvatarDropdown';

// Helper: detect stale-deployment chunk errors (file gone after new deploy)
const isChunkError = (msg?: string) =>
  !!msg && (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('MIME type') ||
    msg.includes('error loading dynamically imported module')
  );

if (typeof window !== 'undefined') {
  // vite:preloadError fires IMMEDIATELY when a lazy chunk 404s — before React sees it.
  // This is the fastest possible way to catch a stale-deployment reload.
  window.addEventListener('vite:preloadError', () => {
    window.location.reload();
  });

  // Belt-and-suspenders: also catch via unhandledrejection (fires after promise rejects)
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    // Silently ignore NotAllowedError from video autoplay - this is expected browser behavior
    if (event.reason?.name === 'NotAllowedError' ||
        event.reason?.message?.includes('not allowed') ||
        event.reason?.message?.includes('user agent') ||
        event.reason?.message?.includes('user denied permission')) {
      event.preventDefault();
      return;
    }

    // Chunk load error → reload immediately (stale deployment)
    if (isChunkError(event.reason?.message)) {
      event.preventDefault();
      window.location.reload();
      return;
    }

    // Let other errors through for debugging
    console.error('Unhandled promise rejection:', event.reason);
  });
}

// Eager load critical pages
import Landing from './pages/Landing';
import Download from './pages/Download';

// Lazy load non-critical pages.
// For chunk errors (stale deployment), skip retries and reload immediately —
// retrying a 404'd file 3× just delays the inevitable and makes the crash visible.
const retryImport = (importFn: () => Promise<any>, retries = 2) => {
  return new Promise((resolve, reject) => {
    importFn()
      .then(resolve)
      .catch((error) => {
        // Chunk gone after new deploy → reload now, don't waste time retrying
        if (isChunkError(error?.message)) {
          window.location.reload();
          return;
        }
        if (retries === 0) {
          reject(error);
          return;
        }
        console.warn(`Import failed, retrying... (${retries} attempts left)`);
        setTimeout(() => {
          retryImport(importFn, retries - 1).then(resolve, reject);
        }, 800);
      });
  });
};

const Dashboard = lazy(() => retryImport(() => import('./pages/Dashboard')));
const MyDrive = lazy(() => retryImport(() => import('./pages/MyDrive')));
const Pricing = lazy(() => retryImport(() => import('./pages/Pricing')));
const StorageInfo = lazy(() => retryImport(() => import('./pages/StorageInfo')));
const Billing = lazy(() => retryImport(() => import('./pages/Billing')));
const Profile = lazy(() => retryImport(() => import('./pages/Profile')));
const Successors = lazy(() => retryImport(() => import('./pages/Successors')));
const Settings = lazy(() => retryImport(() => import('./pages/Settings')));
const AcceptFolderInvite = lazy(() => retryImport(() => import('./pages/AcceptFolderInvite')));
const AcceptSuccessor = lazy(() => retryImport(() => import('./pages/AcceptSuccessor')));
const InheritedVault = lazy(() => retryImport(() => import('./pages/InheritedVault')));
const EditorDoc = lazy(() => retryImport(() => import('./pages/EditorDoc')));
const EditorSheet = lazy(() => retryImport(() => import('./pages/EditorSheet')));
const EditorPdf = lazy(() => retryImport(() => import('./pages/EditorPdf')));
const EditorPpt = lazy(() => retryImport(() => import('./pages/EditorPpt')));
const EditorCode = lazy(() => retryImport(() => import('./pages/EditorCode')));
const WhatsNew = lazy(() => retryImport(() => import('./pages/WhatsNew')));
const AuthVerify = lazy(() => retryImport(() => import('./pages/AuthVerify')));
const ResetPassword = lazy(() => retryImport(() => import('./pages/ResetPassword')));
const ForgotPassword = lazy(() => retryImport(() => import('./pages/ForgotPassword')));
const LegacyActivate = lazy(() => retryImport(() => import('./pages/LegacyActivate')));
const Compare = lazy(() => retryImport(() => import('./pages/Compare')));
const Privacy = lazy(() => retryImport(() => import('./pages/Privacy')));
const Terms = lazy(() => retryImport(() => import('./pages/Terms')));
const About = lazy(() => retryImport(() => import('./pages/About')));
const Admin = lazy(() => retryImport(() => import('./pages/Admin')));

// Animated padlock loader — shackle lifts and drops on loop
const PageLoader = () => (
  <div className="min-h-screen bg-background-light flex items-center justify-center">
    <style>{`
      @keyframes ws-unlock {
        0%, 15%  { transform: translateY(0px); }
        40%, 60% { transform: translateY(-11px); }
        85%, 100% { transform: translateY(0px); }
      }
      @keyframes ws-keyhole {
        0%, 15%  { opacity: 0.18; }
        40%, 60% { opacity: 0.55; }
        85%, 100% { opacity: 0.18; }
      }
      .ws-shackle { animation: ws-unlock 2.8s cubic-bezier(0.4,0,0.2,1) infinite; }
      .ws-keyhole  { animation: ws-keyhole  2.8s ease-in-out infinite; }
    `}</style>
    <svg width="54" height="68" viewBox="0 0 54 68" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Shackle — behind body so the body hides its base when closed */}
      <path
        className="ws-shackle"
        d="M 14 29 C 14 7 40 7 40 29"
        stroke="#334155" strokeWidth="6" strokeLinecap="round" fill="none"
      />
      {/* Lock body — sits on top, covers shackle base in closed position */}
      <rect x="5" y="26" width="44" height="38" rx="9" fill="#0f172a"/>
      {/* Keyhole */}
      <circle cx="27" cy="43" r="5.5" fill="white" className="ws-keyhole"/>
      <rect x="24.5" y="43" width="5" height="8" rx="2.5" fill="white" className="ws-keyhole"/>
    </svg>
  </div>
);

type MeResponse = { user: null | { id: string; email: string } };
type RequestLinkResponse = { ok: boolean; dev?: boolean; verifyUrl?: string; error?: string; details?: string };

const App: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isAuthLoading, setIsAuthLoading] = useState<boolean>(true);
  const [user, setUser] = useState<{ id: string; email: string } | null>(null);

  // Check auth status. If isBackground=true, don't log out on transient errors.
  const refreshMe = async (isBackground = false) => {
    try {
      const res = await fetch('/api/me', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.user) {
          setIsLoggedIn(true);
          setUser(data.user);
        } else if (!isBackground) {
          // Only clear auth on initial load, not background refreshes
          setIsLoggedIn(false);
          setUser(null);
        }
      } else if (res.status === 401) {
        // Explicit unauthorized - user session is invalid
        setIsLoggedIn(false);
        setUser(null);
      } else if (!isBackground) {
        // Server error on initial load - treat as not logged in
        setIsLoggedIn(false);
        setUser(null);
      }
      // For background refreshes with non-401 errors, keep existing auth state
    } catch {
      // Network error - only clear auth on initial load
      if (!isBackground) {
        setIsLoggedIn(false);
        setUser(null);
      }
    } finally {
      setIsAuthLoading(false);
    }
  };

  useEffect(() => {
    refreshMe(false); // Initial load - not background
    // Listen for auth changes (password login from Auth page)
    const onAuthChange = () => refreshMe(false);
    window.addEventListener('wesaves_auth_change', onAuthChange);
    // When user returns to tab (e.g., after clicking magic link email), re-check auth
    // Use background mode to avoid logging out on transient errors
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshMe(true);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('wesaves_auth_change', onAuthChange);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const handleLogin = async (email: string): Promise<{ verifyUrl?: string }> => {
    // First check if user is already authenticated
    if (isLoggedIn) {
      // User is already logged in, no need for magic link
      return { verifyUrl: undefined };
    }
    
    // Magic link: request link, then user clicks email to complete auth.
    const res = await fetch('/api/auth/request-link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.ok === false) {
      const errorMsg = data.message || data.error || 'Failed to request magic link';
      console.error('Magic link request failed:', { status: res.status, data });
      throw new Error(errorMsg);
    }

    return { verifyUrl: data.dev?.verifyUrl };
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggedIn(false);
      setUser(null);
    }
  };

  if (isAuthLoading) {
    return <PageLoader />;
  }

  return (
    <Router>
      <Suspense fallback={<PageLoader />}>
        <Routes>
        <Route path="/" element={<Landing isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />} />
        <Route path="/auth/:type" element={<Landing isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />} />
        <Route path="/whats-new" element={<WhatsNew isLoggedIn={isLoggedIn} onLogout={handleLogout} />} />
        <Route path="/auth/verify" element={<AuthVerify />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify-email" element={<Navigate to="/drive?verified=1" replace />} />
        <Route path="/pricing" element={<Pricing isLoggedIn={isLoggedIn} onLogin={() => {}} />} />
        <Route path="/storage" element={<StorageInfo isLoggedIn={isLoggedIn} />} />
        <Route path="/download/:id" element={<Download />} />
        <Route path="/billing" element={<Billing isLoggedIn={isLoggedIn} onLogout={handleLogout} />} />
        <Route path="/dashboard" element={<Dashboard isLoggedIn={isLoggedIn} onLogout={handleLogout} />} />
        <Route path="/drive" element={<MyDrive isLoggedIn={isLoggedIn} onLogout={handleLogout} />} />
        <Route path="/drive/folder/:folderId" element={<MyDrive isLoggedIn={isLoggedIn} onLogout={handleLogout} />} />
        <Route path="/drive/file/:fileId" element={<MyDrive isLoggedIn={isLoggedIn} onLogout={handleLogout} />} />
        <Route path="/accept-folder-invite/:token" element={<AcceptFolderInvite isLoggedIn={isLoggedIn} />} />
        <Route path="/accept-successor/:token" element={<AcceptSuccessor isLoggedIn={isLoggedIn} />} />
        <Route path="/inherited-vault" element={<InheritedVault isLoggedIn={isLoggedIn} onLogout={handleLogout} />} />
        <Route path="/inherited-vault/:ownerId" element={<InheritedVault isLoggedIn={isLoggedIn} onLogout={handleLogout} />} />
        <Route path="/legacy/activate/:token" element={<LegacyActivate />} />
        <Route path="/profile" element={<Profile isLoggedIn={isLoggedIn} onLogout={handleLogout} />} />
        <Route path="/successors" element={<Successors isLoggedIn={isLoggedIn} onLogout={handleLogout} />} />
        <Route path="/settings" element={<Settings isLoggedIn={isLoggedIn} onLogout={handleLogout} />} />
        <Route path="/editor/doc" element={<EditorDoc />} />
        <Route path="/editor/sheet" element={<EditorSheet />} />
        <Route path="/editor/pdf" element={<EditorPdf />} />
        <Route path="/editor/ppt" element={<EditorPpt />} />
        <Route path="/editor/code" element={<EditorCode />} />
        <Route path="/compare" element={<Compare isLoggedIn={isLoggedIn} onLogout={handleLogout} />} />
        <Route path="/compare/:competitor" element={<Compare isLoggedIn={isLoggedIn} onLogout={handleLogout} />} />
        <Route path="/privacy" element={<Privacy isLoggedIn={isLoggedIn} onLogout={handleLogout} />} />
        <Route path="/terms" element={<Terms isLoggedIn={isLoggedIn} onLogout={handleLogout} />} />
        <Route path="/about" element={<About isLoggedIn={isLoggedIn} onLogout={handleLogout} />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </Router>
  );
};

export default App;
